import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, first, map, tap } from 'rxjs/operators';
import { ProductTypeEnum, UserProfile, UserRepository } from '../core';
import { BaseService } from './base.service';


/**
* This service handles user profile data.
*/
@Injectable({ providedIn: 'root' })
export class UserService extends BaseService {

	private readonly userRepo = inject(UserRepository);

	private readonly CUSTOMER_CARE = 'CustomerCare';
	private readonly _currentUser$ = new BehaviorSubject<UserProfile | null>(null);


	/**
	* Get user first name and last name.
	*/
	getUserName(): Observable<{ firstName: string, lastName: string }> {
		return this.getCurrentUser().pipe(map(user => ({ firstName: user.firstName, lastName: user.lastName })));
	}

	/**
	* Get user country code.
	*/
	getUserCountry(): Observable<string> {
		return this.getCurrentUser().pipe(map(user => user.country.code));
	}

	/**
	* Get current user.
	*/
	getCurrentUser(): Observable<UserProfile> {
		return this._currentUser$.asObservable().pipe(first(user => !!user));
	}

	/**
	* Check if user has a JPS DEMO product.
	*/
	isJPSDemoProduct(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			filter(user => !!user?.products),
			map(user => user!.products.some(p => p.type == ProductTypeEnum.JPS && p.demo))
		);
	}

	/**
	* Check if user has Platform DEMO product.
	*/
	isPlatformDemo(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			filter(user => !!user?.products),
			map(user => user!.products.some(p => p.type == ProductTypeEnum.Platform && p.demo))
		);
	}

	/**
	* Check if user is JPS Extension.
	*/
	isJPSExtension(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			filter(user => !!user?.products),
			map(user => user!.products.some(p => p.type == ProductTypeEnum.JPS && p.version == "1.1"))
		);
	}

	/**
	* Check if user is Customer Care.
	*/
	isCustomerCare(): Observable<boolean> {
		return this._currentUser$.asObservable().pipe(
			first(user => !!user?.roles),
			map(user => user!.roles.includes(this.CUSTOMER_CARE))
		);
	}

	/**
	* Load user profile.
	*/
	loadUser(): Observable<UserProfile> {
		return this.getUserProfile().pipe(first(user => !!user), tap(user => this._currentUser$.next(user)));
	}


	private getUserProfile(): Observable<UserProfile> {
		return this.userRepo.getUserProfile().pipe(
			map(res => this.handleApiResponse(res))
		);
	}

	/**
	 * Get application URL.
	 */
	getApplicationUrl(): Observable<string> {
		return this.userRepo.getApplicationUrl().pipe(
			map(res => this.handleApiResponse(res))
		);
	}

}
